import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import FontAwesome from 'utils/FA';

import './styles/placeholder.scss';

const Placeholder = {};

const defaultLoading = () => (
  <div className='placeholder-loading'>
    <FontAwesome name='spinner' spin />
    &nbsp;Loading...
  </div>
);

/**
 * Returns a component that can be used to render a loading message that changes over time, to be passed as the
 * `loading` prop to a placeholder.
 *
 * @example
 * <Placeholder.loading ready={false}
 *   loading={timedLoading(elapsed => (elapsed >= 10 ? "Please wait a little longer..." : "Loading...")}/>
 *
 * @param timedMessage {function(number):string} returns a message given an elapsed time in seconds
 * @returns {function()} loading component
 */
const timedLoading = (timedMessage = () => 'Loading...') => () => {
  const [message, setMessage] = useState(timedMessage(0));
  const startMs = new Date().getTime();

  useEffect(() => {
    const intervalId = setInterval(() => {
      const elapsedMs = new Date().getTime() - startMs;
      const newMessage = timedMessage(Math.floor(elapsedMs / 1000));
      setMessage(newMessage);
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (<div className='placeholder-loading'>
    <FontAwesome name='spinner' spin />
    &nbsp;{message}
  </div>);
};

/**
 * Shows a placeholder white content is loading
 *
 * Be aware that React still executes javascript in child components,
 * which seems counter-intuitive to developers and could lead to blow-ups.  i.e.
 * ```
 * <Placeholder.loading ready={customer != null}>
 *   Customer {customer.name} has successfully loaded
 * </Placeholder.loading>
 * ```
 * The code above will only be inserted into the DOM if `customer` is not null, but the javascript will execute regardless, and `customer.name` will blow up.  Instead, you can do:
 *
 * ```
 * <Placeholder.loading ready={customer != null} content={ () =>
 *   <div>Customer {customer.name} has successfully loaded</div>
 * }/>
 * ```
 * The `content` method will only be invoked after `ready` is true
 *
 * @param ready     boolean to
 * @param children  child react components (use either this or `content`)
 * @param content   method to invoke in order to render child components once ready (use either this or children)
 * @param loading   method to render a custom view while loading.  Default is a spinner and the text "Loading...".
 * @returns either the contents/children or a loading message
 */
Placeholder.loading = ({ready, children, content, loading=defaultLoading}) => {
  return ready
    ? <React.Fragment>{content ? content() : children}</React.Fragment>
    : loading();
};

Placeholder.select = ({ready, children, ...props}) => {
  return ready
    ? React.Children.only(children)
    : (<select className='Placeholder-select' disabled {...props}/>);
};

Placeholder.SAFroala = ({ready, children, ...props}) => {
  const Child = React.Children.only(children);

  return ready
    ? Child
    : <Child.type {...Child.props} key={'sa-froala-placeholder'} value={ReactDOMServer.renderToString(<TextPlaceholder {...props}/>)}/>;
};

Placeholder.table = ({ ready, children, content, ...props }) => {
  if (ready) {
    return content ? content() : React.Children.only(children);
  }

  return (
    <caption>
      {<TextPlaceholder width={1000} {...props} />}
    </caption>
  );
};

Placeholder.text = ({ready, children, content, ...props}) => {
  return ready
    ? (content ? content() : React.Children.only(children))
    : <TextPlaceholder {...props}/>;
};

Placeholder.formField = ({ready, children, content, prompt, ...props}) => {
  return ready
    ? (content ? content() : React.Children.only(children))
    : <div className={'sa-form-field-group sa-form-tuition-group-header-row'}>
      <div className={'sa-form-field-group-prompt'}>
        {!!prompt &&
          <div className={'sa-form-tuition-group-header'}>
            {prompt}
          </div>
        }
      </div>
      <div className={'sa-form-field-group-body'}>
        <TextPlaceholder width={400} {...props}/>
      </div>
    </div>;
};

/**
 * Just hide this element and pop it in when loading is done.
 * Useful for cases when two components next to each other need to load at the same time and you only want to show loading state from one.
 *
 * @param props  all the same params as Placeholder.loading
 * @returns {*}
 */
Placeholder.invisible = (props) => Placeholder.loading({...props, loading: () => null})

const TextPlaceholder = ({width, lines = 5, lineHeight = 18, spacerHeight = 6, lineWidths = [95, 87, 90, 97, 92], lastLineWidth = 65, ...props}) => {
  const {className} = props;
  const height = (lineHeight + spacerHeight) * lines;
  const bgWidth = width * 1.75;

  return <div
    {...props}
    className={`${className ? className : ''} placeholder-text`}
    style={{
      backgroundSize: `${bgWidth}px`,
      height: `${height}px`,
      top: `-${spacerHeight}px`
    }}
  >
    {
      Array.from(Array(lines).keys()).map(line => (
        <div key={line}>
          <div
            className={`background-masker content-line`}
            style={{
              height: `${spacerHeight}px`,
              top: `${line * (lineHeight + spacerHeight)}px`
            }}
          />
          <div
            className={`background-masker content-end`}
            style={{
              height: `${lineHeight + spacerHeight}px`,
              top: `${line * (lineHeight + spacerHeight)}px`,
              left: `${(line === lines - 1) ? (width * lastLineWidth / 100 ) : (width * lineWidths[line % lineWidths.length] / 100)}px`
            }}
          />
        </div>
      ))
    }
  </div>;
};

export default Placeholder;
export { TextPlaceholder, timedLoading };
